import React, { useEffect } from "react";
import useQrCode from "modules/master-data/fleet/detail/hooks/useQrCode";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { TransportResourceFleetRes } from "_services/modules/transport-resource/fleet";
import { dateFormatter } from "_helper/timeFormatter";
import { toast } from "react-toastify";

interface DetailInfoCardProps {
  detail: TransportResourceFleetRes;
  t: any;
  onEdit: () => void;
  onHistory: () => void;
}

const DetailInfoCard: React.FC<DetailInfoCardProps> = ({ detail, t, onEdit, onHistory }) => {
  const associatedAuxNames = detail.associated_auxiliaries && detail.associated_auxiliaries.length > 0
    ? detail.associated_auxiliaries.map((aux: any) => aux.name || aux.Name).filter(Boolean).join(", ")
    : "—";

  const { qrSrc, generate, print } = useQrCode(null, { width: 200, margin: 6 });

  const handlePrintQRCode = async () => {
    try {
      if (!detail?.id && !detail?.plate_number) {
        toast.error("Missing fleet ID");
        return;
      }
      const qrData = String(detail.id ?? detail.plate_number);
      await generate(qrData);
      await print(qrData, detail.plate_number);
    } catch (error) {
      toast.error("Failed to generate QR code");
    }
  };

  useEffect(() => {
    if (detail?.id || detail?.plate_number) {
      const qrData = String(detail.id ?? detail.plate_number);
      generate(qrData).catch(() => {});
    }
  }, [detail?.id, detail?.plate_number, generate]);

  const infoItems = [
    { label: t("label-vehicle-type"), value: detail.vehicle_type },
    { label: t("label-vehicle-model"), value: detail.vehicle_model },
    { label: t("label-freezer-model"), value: associatedAuxNames },
    { label: t("label-odometer"), value: `${(detail.odometer || 0).toLocaleString()} ${t("unit-km")}` },
    { label: t("label-associated-vendor"), value: detail.vendor_name },
    { label: t("label-stnk-expiry"), value: dateFormatter(detail.stnk_expiry) },
    { label: t("label-kir-expiry"), value: dateFormatter(detail.kir_expiry) },
    { label: t("label-last-maintenance"), value: dateFormatter(detail.last_maintenance_date) },
    // { label: t("label-driver"), value: normalizeDriverName(detail.driver_name, detail.driver_id, t("unassigned") || "Unassigned") },
    // { label: t("label-driver-id"), value: isDriverAssigned(detail.driver_id, detail.driver_name) ? detail.driver_id : "—" },
  ];

  return (
    <div className="bg-white px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="heading5" className="font-bold text-primary-900 uppercase tracking-wider">
          {t("section-fleet-information")}
        </Typography>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={<Icon name="clock" size={15} />}
            onClick={onHistory}
            data-testid="btn-view-history"
          >
            {t("btn-history-data")}
          </Button>
          <Button
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={<Icon name="export" size={15} />}
            onClick={() => window.print()}
            data-testid="btn-export-fleet"
          >
            {t("btn-export")}
          </Button>
          <Button
            variant="fill"
            color="primary"
            size="medium"
            iconLeft={<Icon name="edit-2" size={16} />}
            onClick={onEdit}
            data-testid="btn-edit-fleet"
          >
            {t("btn-edit")}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border p-6 shadow-sm">
        <div className="flex items-center gap-6 pb-6 border-b border-dashed border-border">
          <div className="h-20 w-20 rounded-full overflow-hidden border border-border bg-whiteScale-95 flex-shrink-0">
            <img
              src={detail.photo_url || "https://placehold.co/400x400?text=No+Image"}
              alt={detail.plate_number}
              className="h-full w-full object-cover"
              data-testid="img-fleet-preview"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="heading4" className="font-bold text-slate-900">
              {detail.plate_number}
            </Typography>
            <Typography variant="body" className="text-slate-400 font-medium">
              {detail.hull_number || "—"}
            </Typography>
          </div>
          <div className="ml-auto">
            <div
              data-testid="badge-fleet-status"
              className={`px-4 py-1 rounded-full text-xs font-bold border ${detail.status === "Available" ? "bg-green-50 text-green-600 border-green-100" :
                detail.status === "Assigned" ? "bg-amber-50 text-amber-600 border-amber-100" :
                detail.status === "Maintenance" ? "bg-red-50 text-red-600 border-red-100" :
                detail.status === "Inactive" ? "bg-slate-50 text-slate-500 border-slate-200" :
                  "bg-slate-50 text-slate-500 border-slate-200"
                }`}
            >
              {detail.status ? t(`status-${detail.status.toLowerCase()}` as any) : "—"}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-start gap-6 pt-6">
          <div className="grid grid-cols-4 gap-y-6 pt-6 flex-grow">
            {infoItems.map((item, index) => (
              <div key={index} className="flex flex-col gap-1">
                <Typography variant="bodyMedium" className="text-slate-400 font-medium">{item.label}</Typography>
                <Typography variant="body" className="font-bold text-slate-900">{item.value || "—"}</Typography>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1 pt-6">
            <Typography variant="bodyMedium" className="text-slate-400 font-medium">{t("label-fleet-qr") || "Fleet QR"}</Typography>
            <div className="flex items-center gap-3">
              {qrSrc ? (
                <img src={qrSrc} alt="Fleet QR" className="w-20 h-20 object-contain border border-border rounded" />
              ) : (
                <div className="w-20 h-20 bg-slate-50 border border-border rounded" />
              )}
              <Button
                variant="outline"
                color="tertiary"
                size="medium"
                iconLeft={<Icon name="printer" size={14} />}
                onClick={handlePrintQRCode}
                data-testid="btn-print-qr-code-inline"
              >
                {t("btn-print-qr-code")}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DetailInfoCard;
